import { FC, useEffect, useState } from 'react';

import { Key } from 'lucide-react';
import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import Pagination from '@mui/material/Pagination';

import css from './SameFingerprintUsers.module.css';

import { userProfileService } from 'v2/api/userProfileService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { ProfileCard } from 'v2/components/profileCard/ProfileCard';
import { Select } from 'v2/components/select/Select';
import { UserLink } from 'v2/components/userLink/UserLink';
import { usePagination } from 'v2/hooks/usePagination';
import { SelectOption } from 'v2/types';
import { UserFingerprintDto } from 'v2/types/UserProfileModel';
import { getPaginationCount } from 'v2/utils';

interface SameFingerprintUsersProps {
  fingerprints: UserFingerprintDto[] | undefined;
  userId: string;
}

export const SameFingerprintUsers: FC<SameFingerprintUsersProps> = ({ fingerprints, userId }) => {
  const { page, pageSize, onPageChange } = usePagination({ defaultPageSize: 10 });

  const [selectedFpId, setSelectedFpId] = useState('');

  const fps = fingerprints ?? [];

  useEffect(() => {
    setSelectedFpId('');
    onPageChange(1);
  }, [userId, onPageChange]);

  useEffect(() => {
    if (fingerprints != null && fingerprints.length > 0) {
      setSelectedFpId((prev) => {
        if (prev === '' || !fingerprints.some((fp) => fp.fingerprintId === prev)) {
          return fingerprints[0].fingerprintId;
        }

        return prev;
      });
    }
  }, [fingerprints]);

  const handleFingerprintChange = (value: string) => {
    setSelectedFpId(value);
    onPageChange(1);
  };

  const hasMultipleFp = fps.length > 1;
  const singleFpId = fps.length === 1 ? fps[0].fingerprintId : undefined;

  const activeFpId =
    selectedFpId !== '' && fps.some((fp) => fp.fingerprintId === selectedFpId)
      ? selectedFpId
      : (fps[0]?.fingerprintId ?? '');

  const title =
    hasMultipleFp || singleFpId == null
      ? 'Players with same Fingerprint'
      : `Players with same Fingerprint (${singleFpId})`;
  const fingerprintOptions: SelectOption<string>[] = fps.map((fp) => ({
    id: fp.fingerprintId,
    label: fp.fingerprintId,
    value: fp.fingerprintId,
  }));

  const { data } = useSWR(
    activeFpId !== ''
      ? ['getUsersByFingerprint', page, pageSize, { fingerprintId: activeFpId, excludeUserId: userId }]
      : null,
    userProfileService.getUsersByFingerprint,
    { keepPreviousData: true }
  );

  if (fingerprints === undefined) {
    return (
      <ProfileCard title="Players with same Fingerprint" icon={<Key width={18} height={18} />}>
        <Loader />
      </ProfileCard>
    );
  }

  if (isEmpty(fps)) {
    return (
      <ProfileCard title="Players with same Fingerprint" icon={<Key width={18} height={18} />}>
        <EmptyPlaceholder />
      </ProfileCard>
    );
  }

  return (
    <ProfileCard title={title} icon={<Key width={18} height={18} />}>
      {hasMultipleFp && (
        <Select
          label="Fingerprint"
          value={activeFpId}
          options={fingerprintOptions}
          onChange={handleFingerprintChange}
          size="small"
        />
      )}

      {isNonNullish(data) && (
        <>
          {isEmpty(data.result) ? (
            <EmptyPlaceholder />
          ) : (
            <>
              {data.result.map((user) => (
                <UserLink key={user.id} userId={user.id} nickname={user.userNickname} imgUrl={user.imgUrl} />
              ))}

              {data.total > pageSize && (
                <Pagination
                  className={css.pagination}
                  page={page}
                  count={getPaginationCount(pageSize, data.total)}
                  onChange={(_, p) => onPageChange(p)}
                />
              )}
            </>
          )}
        </>
      )}
    </ProfileCard>
  );
};
